import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/label';

/**
 * Panak (Figure) - Stick figure with image body parts
 *
 * Layout: 1 portrait page. 6 body segments in vertical stack (center),
 * 2 arms (left/right), head as ellipse split in two halves.
 * Each segment shows an image from the input list.
 *
 * Migrated from C# EduFrame.Img10.Generators.Panak
 */
export class Panak implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage();
    const head = 2.5;
    const margin = PdfUnit.FromMillimeters(10);
    const sz = (PdfSize.A4.height - 2 * margin) / (6 + head);
    const size = { width: sz * 1.4, height: sz };

    const allBodyTexts = opts.list.slice(0, 8).map((item) => item.left.getText());
    const bodyCellSize = new PdfSize(size.width, size.height);
    const bodyFont = page.adjustFontMulti(
      allBodyTexts.map((t) => [t]),
      GUtil.baseFont,
      bodyCellSize
    );

    const e = sz * (head + 0.2);
    const ee = e * 0.42;

    const allEyeTexts = opts.list.slice(8, 10).map((item) => item.left.getText());
    const eyeCellSize = new PdfSize(ee, ee);
    const eyeFont = page.adjustFontMulti(
      allEyeTexts.map((t) => [t]),
      GUtil.baseFont,
      eyeCellSize
    );

    let index = 0;

    const r1 = new PdfRect(
      (PdfSize.A4.width - size.width) / 2 - size.width,
      PdfSize.A4.height - margin - 5 * size.height,
      size.width,
      size.height
    );
    const r2 = new PdfRect(
      (PdfSize.A4.width - size.width) / 2 + size.width,
      PdfSize.A4.height - margin - 5 * size.height,
      size.width,
      size.height
    );

    const e0 = new PdfRect((PdfSize.A4.width - e) / 2, margin, e, e);
    const e1 = new PdfRect(PdfSize.A4.width / 2 - ee, e0.centerY - ee / 2, ee, ee);
    const e2 = new PdfRect(PdfSize.A4.width / 2, e0.centerY - ee / 2, ee, ee);

    // Body segments (6 stacked vertically)
    for (let i = 0; i < 6; i++) {
      const r = new PdfRect(
        (PdfSize.A4.width - size.width) / 2,
        PdfSize.A4.height - margin - (i + 1) * size.height,
        size.width,
        size.height
      );
      page.drawRectangle(GUtil.basePen, null, r);
      page.drawLabel(opts.list[index]?.left, { rect: r, font: bodyFont });
      index++;
    }

    // Arms
    page.drawRectangle(GUtil.basePen, null, r1);
    page.drawRectangle(GUtil.basePen, null, r2);
    page.drawLabel(opts.list[index]?.left, { rect: r1, font: bodyFont });
    index++;
    page.drawLabel(opts.list[index]?.left, { rect: r2, font: bodyFont });
    index++;

    // Head - white ellipse background
    page.drawEllipse(null, PdfBrush.White, e0);

    // Eyes (two halves)
    page.drawLabel(opts.list[index]?.left, { rect: e1, font: eyeFont });
    index++;
    page.drawLabel(opts.list[index]?.left, { rect: e2, font: eyeFont });

    // Head outline
    page.drawLine(
      GUtil.basePen,
      new PdfPoint(e0.centerX, e0.y),
      new PdfPoint(e0.centerX, e0.bottom)
    );
    page.drawEllipse(GUtil.basePen, null, e0);
  }
}
